import { use } from "react";
import { BikeContext } from "../context/bikeContext";
import {
  CountingFrom,
  PostSettingsBody,
  ShockSettings as SS,
} from "../http/onlyclicksApi";
import { translate } from "../i18n";
import { Language } from "../types";
import styles from "./ForkSettings.module.css";
import { UseFormReturn } from "./Form";
import Panel from "./Panel";
import Setting from "./Setting";

interface Props {
  shock?: Omit<SS, "id" | "created_at">;
  isEditMode: boolean;
  form: UseFormReturn<PostSettingsBody>;
  countingFrom: CountingFrom;
}

const ShockSettings = ({ shock, isEditMode, form, countingFrom }: Props) => {
  const { bike } = use(BikeContext);
  const i18n = translate(Language.English);
  const mode = isEditMode ? "edit" : "view";

  if (!bike) return null;

  const clicksMinValue = () => {
    switch (countingFrom) {
      case "fully_closed":
        return -40;
      case "fully_open":
        return 0;
    }
  };

  const clicksMaxValue = () => {
    switch (countingFrom) {
      case "fully_closed":
        return 0;
      case "fully_open":
        return 40;
    }
  };

  const validateClicks = (value: number | undefined) => {
    if (value && (value < clicksMinValue() || value > clicksMaxValue())) {
      return `value must be between ${clicksMinValue()} and ${clicksMaxValue()}`;
    }
    return undefined;
  };

  return (
    <Panel title={i18n.shock}>
      {bike.shock ? (
        <ol className={styles.foo}>
          {bike.shock.show_sag && (
            <li>
              <form.Field
                name="shock.sag"
                validators={{
                  onBlur: ({ value }) => {
                    if (value && (value < 0 || value > 100)) {
                      return "value must be between 0 and 100";
                    }
                    return undefined;
                  },
                }}
              >
                {(field) => (
                  <Setting
                    key={field.name}
                    label={i18n.sag}
                    value={{ curr: field.state.value, prev: shock?.sag?.curr }}
                    unit="%"
                    minValue={0}
                    maxValue={100}
                    mode={mode}
                    onChange={(sag) => field.handleChange(sag)}
                    countingFrom={countingFrom}
                  />
                )}
              </form.Field>
            </li>
          )}
          {bike.shock.type === "air" && (
            <li>
              <form.Field
                name="shock.psi"
                validators={{
                  onBlur: ({ value }) => {
                    if (value && (value < 0 || value > 350)) {
                      return "value must be between 0 and 350";
                    }
                    return undefined;
                  },
                }}
              >
                {(field) => (
                  <Setting
                    key={field.name}
                    label={i18n.air_pressure}
                    value={{
                      curr: field.state.value,
                      prev: shock?.psi?.curr,
                    }}
                    unit={i18n.psi}
                    minValue={0}
                    maxValue={350}
                    mode={mode}
                    onChange={(psi) => field.handleChange(psi)}
                    countingFrom={countingFrom}
                  />
                )}
              </form.Field>
            </li>
          )}
          {bike.shock.type === "coil" && (
            <li>
              <form.Field
                name="shock.lbs"
                validators={{
                  onBlur: ({ value }) => {
                    if (value && (value < 200 || value > 800)) {
                      return "value must be between 200 and 800";
                    }
                    return undefined;
                  },
                }}
              >
                {(field) => (
                  <Setting
                    label={i18n.spring_weight}
                    value={{
                      curr: field.state.value,
                      prev: shock?.lbs?.curr,
                    }}
                    unit={i18n.lbs}
                    minValue={200}
                    maxValue={800}
                    mode={mode}
                    onChange={(lbs) => field.handleChange(lbs)}
                    countingFrom={countingFrom}
                  />
                )}
              </form.Field>
            </li>
          )}
          {bike.shock.show_lsc && (
            <li>
              <form.Field
                name="shock.lsc"
                validators={{
                  onBlur: ({ value }) => validateClicks(value),
                }}
              >
                {(field) => (
                  <Setting
                    label={i18n.lsc(true)}
                    value={{
                      curr: field.state.value,
                      prev: shock?.lsc?.curr,
                    }}
                    unit={i18n.clicks}
                    minValue={clicksMinValue()}
                    maxValue={clicksMaxValue()}
                    mode={mode}
                    onChange={(lsc) => field.handleChange(lsc)}
                    countingFrom={countingFrom}
                  />
                )}
              </form.Field>
            </li>
          )}
          {bike.shock.show_hsc && (
            <li>
              <form.Field
                name="shock.hsc"
                validators={{
                  onBlur: ({ value }) => validateClicks(value),
                }}
              >
                {(field) => (
                  <Setting
                    label={i18n.hsc(true)}
                    value={{
                      curr: field.state.value,
                      prev: shock?.hsc?.curr,
                    }}
                    unit={i18n.clicks}
                    minValue={clicksMinValue()}
                    maxValue={clicksMaxValue()}
                    mode={mode}
                    onChange={(hsc) => field.handleChange(hsc)}
                    countingFrom={countingFrom}
                  />
                )}
              </form.Field>
            </li>
          )}
          {bike.shock.show_lsr && (
            <li>
              <form.Field
                name="shock.lsr"
                validators={{
                  onBlur: ({ value }) => validateClicks(value),
                }}
              >
                {(field) => (
                  <Setting
                    label={i18n.lsr(bike.shock!.show_hsr, true)}
                    value={{
                      curr: field.state.value,
                      prev: shock?.lsr?.curr,
                    }}
                    unit={i18n.clicks}
                    minValue={clicksMinValue()}
                    maxValue={clicksMaxValue()}
                    mode={mode}
                    onChange={(lsr) => field.handleChange(lsr)}
                    countingFrom={countingFrom}
                  />
                )}
              </form.Field>
            </li>
          )}
          {bike.shock.show_hsr && (
            <li>
              <form.Field
                name="shock.hsr"
                validators={{
                  onBlur: ({ value }) => validateClicks(value),
                }}
              >
                {(field) => (
                  <Setting
                    label={i18n.hsr(true)}
                    value={{
                      curr: field.state.value,
                      prev: shock?.hsr?.curr,
                    }}
                    unit={i18n.clicks}
                    minValue={clicksMinValue()}
                    maxValue={clicksMaxValue()}
                    mode={mode}
                    onChange={(hsr) => field.handleChange(hsr)}
                    countingFrom={countingFrom}
                  />
                )}
              </form.Field>
            </li>
          )}
          {bike.shock.show_volume_spacers && (
            <li>
              <form.Field
                name="shock.volume_spacers"
                validators={{
                  onBlur: ({ value }) => {
                    if (value && (value < 0 || value > 10)) {
                      return "value must be between 0 and 10";
                    }
                    return undefined;
                  },
                }}
              >
                {(field) => (
                  <Setting
                    label={i18n.volume_spacers}
                    value={{
                      curr: field.state.value,
                      prev: shock?.volume_spacers?.curr,
                    }}
                    unit={i18n.quantity}
                    minValue={0}
                    maxValue={10}
                    mode={mode}
                    onChange={(volumeSpacers) =>
                      field.handleChange(volumeSpacers)
                    }
                    countingFrom={countingFrom}
                  />
                )}
              </form.Field>
            </li>
          )}
        </ol>
      ) : (
        <span>Shock not configured.</span>
      )}
    </Panel>
  );
};

export default ShockSettings;
